// Does an Object-Explorer tree node satisfy the components the cursor's dotted
// chain SPELLED OUT?
//
// The three cursor resolvers (`identifierAtCursor` / `routineAtCursor` /
// `columnAtCursor`) each walk the OE tree for same-named nodes and then pick
// one. The pick used to be `exact ?? matches[0]`, so a `B.dbo.users` cursor
// with only `A.dbo.users` in the tree silently chose A. That was harmless
// while the identifier index was per-connection; it is not now that the index
// is per (connection, database) — `referenceTargetAtCursor` routes Find
// References / Peek at the database the chain names, so a hit that fell back
// to another database sent the search to the wrong database's index and the
// tab reported "no references found".
//
// So a component the user TYPED is binding: the node must come from that
// database, and from that schema. A schema-qualified chain also stays inside
// the tab's database — `sales.users` typed from a connection sitting in `A`
// means `A.sales.users`, never `B.sales.users`. Only a BARE name keeps the
// historical "first same-named object anywhere in the tree" fallback, which is
// what lets F12 work before the tab's own database subtree has been expanded;
// routing compensates by never trusting a bare hit's database (it uses the
// tab's — see `referenceTargetAtCursor.ts`).
//
// A BARE name that matches in SEVERAL databases is RANKED, not merely gated:
// `preferContextDatabase` keeps the tab's own database's candidates whenever it
// has any, and only an empty rank lets the cross-database fallback through.
// Without the rank the fallback won on tree order alone — `users.email` typed
// in `B` opened `A.dbo.users` because `A` sorts first. All three resolvers
// share the ranking so they cannot drift; each then adds what only it can
// rank — the column resolver resolves a DECLARED alias BEFORE the ranking runs
// at all (a `FROM … u` alias is a statement-local binding that shadows every
// relation, in the tab's database or a foreign one — `columnAtCursor.ts`), and
// the table/view resolver ranks the REQUESTED schema above the table-vs-view
// kind once the databases tie (`identifierAtCursor.ts`).
//
// What a hit reports as its `explicitDatabase` is the CATALOG spelling of the
// database the typed name bound to (`catalogDatabaseForTyped`), never the
// source spelling: PostgreSQL keys one index and one pool per exact database
// name, so an unquoted `Sales` that matched the tree's `sales` must route at
// `sales` instead of opening a second, case-different lifecycle entry.
//
// Components the TREE leaves empty stay permissive on purpose: SQLite trees,
// and hand-built fixtures, hang objects straight off the connection with no
// database node to compare against.
//
// Every comparison below goes through an engine-aware equality, and WHICH one
// depends on the COMPONENT being compared: a database name and a schema /
// object name are not equally case-significant on the same server, so this
// module exposes two equalities rather than one.
//
// `databaseComponentEquals` — DATABASE names. What it decides is which
// database's identifier INDEX answers
// (`find-references/referenceTargetAtCursor.ts`), so it has to agree with the
// key the lifecycle files that index under
// (`connection-lifecycle/databaseIdentity.ts`):
//  - PostgreSQL / Oracle: EXACT after folding. An unquoted `Sales` folds to
//    `sales` (pg) / `SALES` (oracle) and a quoted `"Sales"` stays `Sales`, so
//    once folded the comparison is byte-exact. PostgreSQL really does host
//    `Sales` and `sales` as two databases, and the lifecycle files one index
//    (and one pool) per exact name — folding them together routed Find
//    References at the other database's index. Oracle's OE "database" node is
//    the OWNER, which the catalog stores already folded, so exact is also the
//    right comparison there.
//  - SQL Server: EXACT when the probed SERVER collation is case-sensitive
//    (`mssqlCollationProbe.ts` → `mssqlDatabaseNamesCaseSensitive`), because
//    the lifecycle then keys `Sales` and `sales` as two databases; otherwise
//    case-insensitive. A collation that is not known YET (probe pending, or it
//    failed) is treated as case-INSENSITIVE: `_CI_` is what almost every
//    instance ships, and the coarser answer only over-includes a tree node —
//    it never invents a second index, since routing takes the CATALOG spelling
//    of whatever node matched (`catalogDatabaseForTyped`).
//  - MySQL: EXACT when the probed `lower_case_table_names` is `0`
//    (`mysqlCaseProbe.ts` → `mysqlObjectKeyCase`), case-insensitive otherwise
//    and while it is not known. A MySQL database is a DIRECTORY on disk, so on
//    the Linux default `DbA` and `dba` really are two databases — and since a
//    MySQL schema IS a database, a typed `dba.users` was satisfied by a `DbA`
//    node the tree happened to list first, which the leaf-only spelling
//    preference cannot undo. Nothing ROUTES on this (MySQL's index identity
//    spans the whole connection), but the node it picks is the object rename
//    and find-references then act on.
//  - SQLite: case-insensitive, and moot — the tree has no database node.
//
// `objectComponentEquals` — SCHEMA and OBJECT names (the leaf, and a column).
// Same shape, two differences that matter:
//  - on SQL Server these follow the collation of the DATABASE that holds them,
//    not the server's, and a `_CI_` instance can host a `_CS_` database. So the
//    per-database answer (`mssqlObjectNamesCaseSensitive`) is read under the
//    database IDENTITY the lifecycle filed it under —
//    `databaseIdentityKey(connectionId, engineId, …)` of the node's own
//    database (`oeDatabaseIdentity` below). Unknown → case insensitive, as
//    above.
//  - on MySQL the answer differs PER COMPONENT, which is why this takes a
//    {@link NameComponent}: `lower_case_table_names = 0` makes schemas
//    (directories) and table/view names (files) case-sensitive, while routine
//    and column names are folded by the server whatever the setting. Every
//    other engine gives one answer for all four, and ignores the argument.
// pg / Oracle stay exact-after-folding, SQLite case-insensitive.
//
// FOLDED vs SPELLED. A typed component arrives in both spellings
// ({@link TypedComponent}) because which one an EXACT comparison may use is
// per engine. On pg / Oracle the FOLDED form is the engine's own
// normalisation — unquoted `Sales` → `sales`/`SALES`, quoted `"Sales"` stays —
// so exact means exact-on-folded. SQL Server and MySQL fold nothing of their
// own: the parser is handed `identifierFolding: 'case-insensitive'`, which
// LOWER-CASES every unquoted component (`sql-resolver/scope/identifiers.ts` →
// `foldIdentifier`), so a typed `dbo.V` arrives folded as `v` and only the
// SOURCE spelling still says `V`. Where those two engines compare exactly (a
// `_CS_` SQL Server database, `lower_case_table_names = 0` on MySQL) the source
// spelling is therefore the side the exact comparison reads.
//
// EXACT-SPELLED TIE-BREAK. Where the policy is case-INSENSITIVE (or not known
// yet) several tree nodes can satisfy the equality at once — a `_CI_` database
// really can hold `dbo.V` and `dbo.v`, a `_CI_` instance is just unable to tell
// them apart by name. `preferExactTypedComponents` then keeps the candidate
// spelled exactly as the user typed it, so `dbo.v` never opens `dbo.V` merely
// because the tree lists it first. It runs over EVERY component the chain
// spelled out, not just the leaf: two candidates can differ only in the
// database or the schema (`B.DBO.users` vs `B.dbo.users`), which a leaf-only
// preference cannot separate. It is a preference, not a gate: with no
// exact-spelled candidate the whole list survives, and on a case-sensitive
// policy the equality has already done the work.
//
// Whether a typed SCHEMA may instead name the node's DATABASE is per engine —
// `databaseNodeIsSchemaEngine` below owns that rule, and it turns on whether
// the engine's OE "database" node IS the schema/owner:
//  - MySQL: YES. A MySQL schema IS a database, and its OE "database" nodes ARE
//    schemas: a live node carries the DATABASE NAME as its schema
//    (`mysqlQueries.ts` selects `TABLE_SCHEMA`/`ROUTINE_SCHEMA AS schema_name`,
//    which `databaseObjectRowMapper.ts` stores as `objectDetails.schema`). So
//    a typed `app.orders` satisfies the schema check on an `orders` node under
//    the `app` DATABASE node, and it is the final `nodeDb === contextDb ||
//    schemaMayNameDatabase` branch that lets the chain leave the tab's
//    database.
//  - Oracle: YES, for the same shape and a different reason — the "database"
//    node is the OWNER (`oracleQueries.ts` lists `ALL_USERS` as databases and
//    stamps objects with `OWNER AS schema_name`), so a node reads
//    `{ database: 'HR', schema: 'HR' }` and a typed `HR.EMPLOYEES` from a
//    `JAMSQL` tab has to be allowed out of the tab's node. Nothing routes on
//    the name: Oracle keys ONE index per connection
//    (`connection-lifecycle/databaseIdentity.ts`), so gating here bought no
//    routing safety and silently killed every cross-owner F12 / Find
//    References.
//  - SQL Server / PostgreSQL: NO. A typed schema keeps the chain inside the
//    tab's (or the typed) database: `sales.users` in database `A` is
//    `A.sales.users`, and a foreign database happening to be named `sales`
//    must not satisfy it — that would send Find References at the wrong
//    database's index.
//  - SQLite: moot — the tree has no database node, so the database checks
//    short-circuit before the exception could apply.

import type { DatabaseEngineId } from '@/core/engines';

import { databaseIdentityKey } from '../connection-lifecycle/databaseIdentity';
import {
  mssqlDatabaseNamesCaseSensitive,
  mssqlObjectNamesCaseSensitive,
} from '../mssqlCollationProbe';
import { mysqlObjectKeyCase } from '../mysqlCaseProbe';

export type OeNodeQualification = {
  /** Database the OE tree attributed to the node — `undefined` when the tree
   *  does not nest objects under a database node. */
  readonly database?: string;
  /** Schema the node's `objectDetails` carries, if any. */
  readonly schema?: string;
};

/** One identifier component of the cursor's chain, in BOTH spellings — which
 *  one an exact comparison may read is per engine (see FOLDED vs SPELLED in the
 *  header). Build it with {@link typedComponent} so a caller cannot supply one
 *  half and silently get the other engine's answer. */
export type TypedComponent = {
  /** Engine-FOLDED (`QualifiedName.foldedDatabase` / `foldedSchema` /
   *  `foldedName`): pg `Sales` → `sales`, oracle → `SALES`, quoted verbatim,
   *  mssql/mysql/sqlite lower-cased unless quoted. */
  readonly folded?: string;
  /** SOURCE spelling (`QualifiedName.database` / `schema` / `name`) — what a
   *  case-sensitive SQL Server compares, since it folds nothing itself. */
  readonly spelled?: string;
};

/** A component of the cursor's chain. `undefined` when the chain didn't spell
 *  that component out at all, so `if (typed.typedDatabase)` still reads as
 *  "the user named a database". */
export function typedComponent(
  folded: string | undefined,
  spelled: string | undefined,
): TypedComponent | undefined {
  if (folded === undefined && spelled === undefined) return undefined;
  return { folded, spelled };
}

/** A CATALOG spelling used on the typed side of a comparison (the tab's own
 *  database against a node's, say). A catalog name is already its own fold, so
 *  both halves are the same string. */
export function catalogComponent(name: string | undefined): TypedComponent | undefined {
  return typedComponent(name, name);
}

export type TypedQualification = {
  /** Database the chain spelled out (`MandTob.dbo.ARD` → `MandTob`). Built
   *  with {@link typedComponent} from `QualifiedName.foldedDatabase` +
   *  `.database`, never the raw source text alone — see FOLDED vs SPELLED. */
  readonly typedDatabase?: TypedComponent;
  /** Schema the chain spelled out (`dbo.ARD` → `dbo`), same shape. */
  readonly typedSchema?: TypedComponent;
  /** The tab's current database — where a chain that names no database
   *  resolves. `null` on surfaces with no current-database binding. */
  readonly contextDatabase?: string | null;
  /** Decides whether a typed SCHEMA may name the node's DATABASE instead
   *  (MySQL only — see the header). Required so the three resolvers must each
   *  state the engine and cannot silently inherit MySQL's exception. */
  readonly engineId: DatabaseEngineId;
  /** Which connection's case policy answers. SQL Server's two collations are
   *  probed per connection (`mssqlCollationProbe.ts`), so every comparison on
   *  that engine needs it; `null` (no connection bound) reads as "policy not
   *  known" — the case-insensitive default. */
  readonly connectionId: string | null;
};

/** Engines whose OE "database" node IS the schema/owner, so a typed SCHEMA
 *  legitimately names a database node — each for its own reason:
 *   - MySQL: a schema IS a database. `mysqlQueries.ts` enumerates databases and
 *     stamps every object with `TABLE_SCHEMA`/`ROUTINE_SCHEMA AS schema_name`,
 *     so a node under `app` reads `{ database: 'app', schema: 'app' }`.
 *   - Oracle: the database node is the OWNER. `oracleQueries.ts` lists
 *     `ALL_USERS` as the explorer's "databases" and stamps every object with
 *     `OWNER AS schema_name`, so a node reads `{ database: 'HR', schema: 'HR' }`
 *     and a typed `HR.EMPLOYEES` from a `JAMSQL` tab must be allowed to leave
 *     the tab's node. Nothing routes on the name there — Oracle keys ONE index
 *     per connection (`connection-lifecycle/databaseIdentity.ts`) — so the gate
 *     would only ever cost a resolution.
 *  SQL Server and PostgreSQL keep the two dimensions apart (see the header). */
function databaseNodeIsSchemaEngine(engineId: DatabaseEngineId): boolean {
  return engineId === 'mysql' || engineId === 'oracle';
}

/** Engines that store identifiers as SPELLED and compare them byte-exactly,
 *  whatever the component: PostgreSQL (unquoted → lower) and Oracle (unquoted →
 *  UPPER) both fold at parse time, so once folded the comparison is exact for
 *  databases, schemas, objects and columns alike. */
function engineFoldsAndComparesExactly(engineId: DatabaseEngineId): boolean {
  return engineId === 'pgsql' || engineId === 'oracle';
}

/** Which half of a {@link TypedComponent} an EXACT comparison reads. pg/oracle
 *  compare the FOLDED half (folding is the engine's own normalisation); SQL
 *  Server and MySQL fold nothing of their own, so only the SOURCE spelling
 *  still carries the case the server compares (the parser lower-cases every
 *  unquoted component there — see FOLDED vs SPELLED in the header). */
function exactSide(engineId: DatabaseEngineId, typed: TypedComponent): string | undefined {
  return engineFoldsAndComparesExactly(engineId) ? typed.folded : typed.spelled;
}

/** Which component of a chain is being compared. MySQL is the only engine
 *  whose answer differs between them — see {@link mysqlComponentCaseSensitive}
 *  — so every other engine ignores it. */
export type NameComponent = 'schema' | 'relation' | 'routine' | 'column';

/** `mysqlObjectKeyCase` answers per OBJECT KIND; this maps the four chain
 *  components onto it. `view` stands for every relation: a MySQL view and a
 *  MySQL table are both FILES, so both follow `lower_case_table_names`, and the
 *  `schema` half of the same answer is kind-independent (it is the DIRECTORY
 *  rule). Routine and column names are folded by the server whatever the
 *  setting, so they are never compared exactly. Reading the policy through
 *  `mysqlObjectKeyCase` — the rule the identifier index keys by — is what keeps
 *  this module from drifting away from the index it feeds. Not probed yet →
 *  `false`, i.e. the over-inclusive case-insensitive default. */
function mysqlComponentCaseSensitive(
  connectionId: string | null | undefined,
  component: NameComponent,
): boolean {
  if (!connectionId || component === 'routine' || component === 'column') return false;
  const policy = mysqlObjectKeyCase(connectionId, 'view');
  if (!policy) return false;
  return component === 'schema' ? policy.schema : policy.name;
}

function insensitiveEquals(typed: TypedComponent, catalog: string): boolean {
  const value = typed.folded ?? typed.spelled;
  return value !== undefined && value.toLowerCase() === catalog.toLowerCase();
}

/** Equality for a DATABASE name — the component that decides which identifier
 *  INDEX a Find References / Peek routes at. Exact on pg/oracle, exact on SQL
 *  Server only while the probed SERVER collation says the instance tells `Sales`
 *  from `sales`, exact on MySQL only while the probed `lower_case_table_names`
 *  is `0` (a database is a directory there), case-insensitive everywhere else —
 *  an unprobed policy on either engine included, since `_CI_` is the common
 *  SQL Server default and over-inclusion is the safe direction for a lookup.
 *  See the header for the full per-engine rationale. A missing component on
 *  either side is never a match; callers decide separately whether "absent"
 *  means permissive. */
export function databaseComponentEquals(
  engineId: DatabaseEngineId,
  connectionId: string | null | undefined,
  typed: TypedComponent | undefined,
  catalog: string | undefined,
): boolean {
  if (!typed || catalog === undefined) return false;
  if (engineFoldsAndComparesExactly(engineId)) return exactSide(engineId, typed) === catalog;
  if (
    engineId === 'mssql' &&
    connectionId &&
    mssqlDatabaseNamesCaseSensitive(connectionId) === true
  ) {
    return exactSide(engineId, typed) === catalog;
  }
  // A MySQL database IS its schema directory, so it takes the schema rule.
  if (engineId === 'mysql' && mysqlComponentCaseSensitive(connectionId, 'schema')) {
    return exactSide(engineId, typed) === catalog;
  }
  return insensitiveEquals(typed, catalog);
}

/** Equality for a SCHEMA, OBJECT or COLUMN name. Same shape as
 *  {@link databaseComponentEquals} with two differences:
 *   - on SQL Server these follow the collation of the DATABASE that holds them
 *     (a `_CI_` instance can host a `_CS_` database), so the answer is read
 *     under that database's lifecycle IDENTITY — pass
 *     {@link oeDatabaseIdentity} of the candidate node's own database.
 *     `undefined` identity, or a policy not probed yet, reads as
 *     case-INSENSITIVE.
 *   - on MySQL the answer depends on WHICH component this is, so `component`
 *     is required: schemas and relation names follow `lower_case_table_names`,
 *     routine and column names never do. Every other engine ignores it. */
export function objectComponentEquals(
  engineId: DatabaseEngineId,
  connectionId: string | null | undefined,
  databaseIdentity: string | undefined,
  typed: TypedComponent | undefined,
  catalog: string | undefined,
  component: NameComponent,
): boolean {
  if (!typed || catalog === undefined) return false;
  if (engineFoldsAndComparesExactly(engineId)) return exactSide(engineId, typed) === catalog;
  if (
    engineId === 'mssql' &&
    connectionId &&
    databaseIdentity !== undefined &&
    mssqlObjectNamesCaseSensitive(connectionId, databaseIdentity) === true
  ) {
    return exactSide(engineId, typed) === catalog;
  }
  if (engineId === 'mysql' && mysqlComponentCaseSensitive(connectionId, component)) {
    return exactSide(engineId, typed) === catalog;
  }
  return insensitiveEquals(typed, catalog);
}

/** The lifecycle identity of the database a tree node sits in — the key SQL
 *  Server's per-database collation was filed under
 *  (`connection-lifecycle/navCasePolicy.ts` probes it under exactly this key),
 *  so {@link objectComponentEquals} cannot read one database's policy for
 *  another's. Falls back to the TAB's database for trees that hang objects
 *  straight off the connection (SQLite, hand-built fixtures); `undefined` when
 *  neither side names one, which reads as "policy not known". */
export function oeDatabaseIdentity(
  engineId: DatabaseEngineId,
  connectionId: string | null | undefined,
  nodeDatabase: string | undefined,
  contextDatabase: string | null | undefined,
): string | undefined {
  if (!connectionId) return undefined;
  const database = nodeDatabase ?? contextDatabase;
  if (!database) return undefined;
  return databaseIdentityKey(connectionId, engineId, database);
}

/** Equality between the TAB's database and a node's. A CATALOG spelling on both
 *  sides, on every engine — which is why it needs no normalisation of its own
 *  and takes {@link databaseComponentEquals} directly:
 *
 *  - the NODE's side is what the tree read out of the catalog;
 *  - the TAB's side is a name the user PICKED from a catalog listing (the
 *    Object-Explorer selection, or the database dropdown — both fed by
 *    `databaseObjectsService.getDatabases`), never free text. On Oracle the
 *    "database" is the OWNER, and the one configured value that could reach a
 *    tab — `oracleInitialSchema` — is quoted VERBATIM into
 *    `ALTER SESSION SET CURRENT_SCHEMA = "…"` by the pool
 *    (`electron/oracle-connection/oraclePoolFactory.ts`) and by every execution
 *    (`electron/query-engine-adapters/oracleAdapter.ts`), so a spelling the
 *    catalog does not hold never gets as far as a connected tab.
 *
 *  It was briefly UPPER-folded on Oracle, to rank a hypothetical `hr` tab
 *  against the catalog's `HR`. That fold is what round 13 removed: `Sales` and
 *  `SALES` are two owners once one was created quoted, the rank here is a
 *  FILTER whose survivor becomes the F2 / Find References target, and the fold
 *  made a `Sales` tab select `SALES`'s table while never matching its own. */
export function contextDatabaseEquals(
  engineId: DatabaseEngineId,
  connectionId: string | null | undefined,
  contextDatabase: string | null | undefined,
  nodeDatabase: string | undefined,
): boolean {
  if (!contextDatabase) return false;
  return databaseComponentEquals(
    engineId,
    connectionId,
    catalogComponent(contextDatabase),
    nodeDatabase,
  );
}

/** Keeps the candidates spelled EXACTLY as the user typed the name, when any
 *  are — the tie-break for a case-INSENSITIVE (or not-yet-known) policy, where
 *  `dbo.V` and `dbo.v` both satisfy the equality and tree order alone used to
 *  decide. A preference, never a gate: with no exact-spelled candidate the list
 *  survives whole, so a `dbo.v` cursor still opens the only `dbo.V` there is.
 *  Engine-agnostic and probe-free — in a case-sensitive catalog the typed
 *  spelling IS the catalog spelling, and in a case-insensitive one the
 *  exact-spelled candidate names the same object as any of its siblings. */
export function preferExactSpelling<T>(
  candidates: readonly T[],
  spelled: string | undefined,
  nameOf: (candidate: T) => string,
): readonly T[] {
  if (spelled === undefined || candidates.length < 2) return candidates;
  const exact = candidates.filter((c) => nameOf(c) === spelled);
  return exact.length > 0 ? exact : candidates;
}

/** {@link preferExactSpelling} over BOTH name components a chain can spell out
 *  inside one database — the schema first, then the leaf. The leaf alone is not
 *  enough: two candidates can differ only in the SCHEMA (`B.DBO.users` next to
 *  `B.dbo.users`), share the leaf, and tree order then chose — so F2 on
 *  `B.dbo.users` renamed `B.DBO.users`, and Find References searched for it.
 *
 *  Same guarantees as the single-component version, component by component: a
 *  PREFERENCE, never a gate (nothing spelled exactly → the pool survives
 *  whole), probe-free (in a case-sensitive catalog the typed spelling IS the
 *  catalog spelling; in a case-insensitive one every survivor names the same
 *  object), and a no-op for a component the chain did not spell out.
 *
 *  The DATABASE is deliberately NOT part of it. Where a database spelling
 *  distinguishes two databases — PostgreSQL always, SQL Server on a `_CS_`
 *  instance, MySQL on `lower_case_table_names = 0` —
 *  {@link databaseComponentEquals} is already EXACT and the gate has done the
 *  work; where it does not, the case-distinct nodes are ONE database and
 *  routing deliberately reports the CATALOG spelling, so that it keys one
 *  lifecycle entry and one pool rather than two ({@link catalogDatabaseForTyped}).
 *  What is left — a `_CS_` instance whose collation has not been probed yet —
 *  is not a tie-break problem but a not-yet-known-policy problem, and rename
 *  handles it by re-resolving once the policy IS known
 *  (`rename/renameProvider.ts`). */
export function preferExactTypedComponents<T>(
  candidates: readonly T[],
  typed: { readonly schema?: string; readonly name?: string },
  read: {
    readonly schema: (candidate: T) => string | undefined;
    readonly name: (candidate: T) => string;
  },
): readonly T[] {
  const pool = preferExactSpelling(candidates, typed.schema, (c) => read.schema(c) ?? '');
  return preferExactSpelling(pool, typed.name, read.name);
}

/** True when `node` is a legitimate resolution of a chain typed as `typed`.
 *  The chain's components arrive as {@link TypedComponent}s and are compared
 *  against the OE catalog spelling with the equality their COMPONENT calls for
 *  — {@link databaseComponentEquals} for the database,
 *  {@link objectComponentEquals} for the schema — so a quoted `"Sales"` keeps
 *  its case, an unquoted `Sales` binds to pg's `sales`, and a case-sensitive
 *  SQL Server database tells `Sales.dbo` from `Sales.DBO`. */
export function matchesTypedQualification(
  node: OeNodeQualification,
  typed: TypedQualification,
): boolean {
  const { engineId, connectionId } = typed;
  const nodeDb = node.database;
  const nodeSchema = node.schema;
  const typedDb = typed.typedDatabase;
  const typedSchema = typed.typedSchema;
  // Which database's collation answers for this node's SCHEMA name — its own,
  // falling back to the tab's for a tree with no database node.
  const identity = oeDatabaseIdentity(engineId, connectionId, nodeDb, typed.contextDatabase);
  // A typed schema may stand for the node's DATABASE only where a schema IS a
  // database. On SQL Server / PostgreSQL / Oracle it must be the node's schema.
  // The catalog side is a DATABASE name there, so it takes the database rule —
  // which on both exception engines (MySQL insensitive, Oracle exact) is the
  // same answer the object rule would give.
  const schemaMayNameDatabase =
    !!typedSchema &&
    databaseNodeIsSchemaEngine(engineId) &&
    databaseComponentEquals(engineId, connectionId, typedSchema, nodeDb);

  if (
    typedSchema &&
    nodeSchema &&
    !objectComponentEquals(engineId, connectionId, identity, typedSchema, nodeSchema, 'schema') &&
    !schemaMayNameDatabase
  ) {
    return false;
  }

  if (!nodeDb) return true;
  if (typedDb) return databaseComponentEquals(engineId, connectionId, typedDb, nodeDb);
  // Bare names keep the cross-database fallback (see the header).
  if (!typedSchema) return true;
  const contextDb = typed.contextDatabase;
  if (!contextDb) return true;
  return contextDatabaseEquals(engineId, connectionId, contextDb, nodeDb) || schemaMayNameDatabase;
}

/** Narrows `candidates` to the ones the TAB's database owns, when it owns any
 *  — the shared rank behind "a bare name means the database I am sitting in".
 *  Returns the input untouched when the surface has no database bound, or when
 *  nothing matched it (the cross-database fallback the header describes; it is
 *  also what keeps trees with no database node — SQLite, fixtures — working,
 *  since `databaseOf` is `undefined` for every candidate there). */
export function preferContextDatabase<T>(
  candidates: readonly T[],
  engineId: DatabaseEngineId,
  connectionId: string | null | undefined,
  contextDatabase: string | null | undefined,
  databaseOf: (candidate: T) => string | undefined,
): readonly T[] {
  if (!contextDatabase) return candidates;
  const inContext = candidates.filter((c) =>
    contextDatabaseEquals(engineId, connectionId, contextDatabase, databaseOf(c)),
  );
  return inContext.length > 0 ? inContext : candidates;
}

/** False only when the tree ATTRIBUTED a database to the hit and it is not the
 *  tab's — i.e. the hit came from the cross-database fallback. `true` when
 *  there is nothing to contradict (no database bound on the surface, or a tree
 *  that hangs objects straight off the connection). */
export function isInContextDatabase(
  nodeDatabase: string | undefined,
  engineId: DatabaseEngineId,
  connectionId: string | null | undefined,
  contextDatabase: string | null | undefined,
): boolean {
  if (!contextDatabase || !nodeDatabase) return true;
  return contextDatabaseEquals(engineId, connectionId, contextDatabase, nodeDatabase);
}

/** A hit's `explicitDatabase`: the CATALOG spelling of the database the typed
 *  name bound to, or `undefined` when the chain named no database (routing then
 *  uses the tab's — a bare hit's own database is never trusted). Falls back to
 *  the source spelling only when the tree could not attribute a database to the
 *  node at all, where there is no catalog spelling to prefer. */
export function catalogDatabaseForTyped(
  typedDatabase: string | undefined,
  nodeDatabase: string | undefined,
): string | undefined {
  if (!typedDatabase) return undefined;
  return nodeDatabase ?? typedDatabase;
}
